<template>
    <div class="details">
        <ProductDetailsHeader :product="product" class="details-header" />

        <LazyImage :src="product.ImageUrl" :alt="product.Description" class="details-image" />

        <div class="details-content">
            <div class="details-content-info">
                <div>
                    <p class="details-content-info-title">
                        {{ product.Description }}
                    </p>
                    <p class="details-content-info-date">
                        {{ onFormatDate(product.Date) }}
                    </p>
                </div>

                <Rating v-if="product.Rating" :rating="product.Rating" />

                <ButtonLink size="m" color="black" :onClick="onActivityDetailsClick">
                    {{ t({ key: 'shared.activity_and_ticket_details' }) }}
                </ButtonLink>
            </div>

            <div
                v-if="product.Tickets && product.Tickets.length > 0"
                class="details-content-price">
                <span class="details-content-price-label">
                    {{ t({ key: 'shared.price', suffix: ':' }) }}
                </span>
                <span class="details-content-price-amount">
                    {{ getMoneyObjectAsString(product.Price) }}
                </span>
                <ButtonLink
                    size="m"
                    color="black"
                    class="details-content-price-link"
                    :onClick="onPriceDetailsClick">
                    {{ t({ key: 'shared.price_details' }) }}
                </ButtonLink>
            </div>

            <CancellationPanel :product="product" />
        </div>

        <Divider v-if="!isDesktop" color="lightGrey" thickness="1" topMargin="xxl" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, PropType } from 'vue';
    import { ActivityProduct } from '@/api/Cart/cart.interfaces';
    import { getLocaleWeekdayLongMonthYear } from '@shared/helpers/datetime/datetime.formatters';
    import { convertToDate } from '@shared/helpers/datetime/datetime';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { capitalizeWords } from '@shared/utils/string';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { ProductModalTab, ModalType } from '@/store/general/types';
    import { useStore } from '@/store';
    import {
        logStatsigCheckoutMoreInformationOpen,
        logStatsigCheckoutPriceDetailsOpen,
    } from '@/tracking/statsig/checkout.functions';
    import LazyImage from '@ui/components/LazyImage/LazyImage.vue';
    import Rating from '@ui/components/Rating/Rating.vue';
    import CancellationPanel from '@/components/checkoutPageV3/Shared/CancellationPanel/CancellationPanel.vue';
    import ProductDetailsHeader from '@/components/checkoutPageV3/Shared/ProductDetailsHeader/ProductDetailsHeader.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';

    export default defineComponent({
        name: 'ActivityDetails',
        methods: { t, getMoneyObjectAsString },
        components: {
            ButtonLink,
            ProductDetailsHeader,
            LazyImage,
            Rating,
            CancellationPanel,
            Divider,
        },
        props: {
            product: {
                type: Object as PropType<ActivityProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();
            const { isDesktop } = useScreenSize();

            function onFormatDate(date: string) {
                return capitalizeWords(getLocaleWeekdayLongMonthYear(convertToDate(date)));
            }

            function onActivityDetailsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Details,
                });

                logStatsigCheckoutMoreInformationOpen(props.product.Type);
            }

            function onPriceDetailsClick() {
                store.dispatch('general/setModal', {
                    type: ModalType.Product,
                    productId: props.product.Id,
                    activeTab: ProductModalTab.Price,
                });

                logStatsigCheckoutPriceDetailsOpen(props.product.Type);
            }

            return {
                LabelFormat,
                onFormatDate,
                isDesktop,
                onActivityDetailsClick,
                onPriceDetailsClick,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Details.scss';
</style>
